import { Component, Inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { InvoiceInterface } from '@contracts/interfaces/invoice.interface';
import { TransactionsService } from 'src/app/services/transactions.service';
import { ProofService } from 'src/app/services/proof.service';
import { SquareTransaction, Transaction } from '@contracts/interfaces/transaction.interfaces';
import { CSVService } from 'src/app/services/csv.service';
import { ACCOUNT } from '../../accounts';
import { JobService } from '../../services/job.service';
import { Job } from '@contracts/interfaces/job.interface';
import { SquarePayoutCandidate, SquareService } from 'src/app/services/square.service';
import { Subscription } from 'rxjs';
import { formatSquareNote } from 'src/app/tools/square-note';

export interface DialogData {
    transaction: Transaction,
}

interface CSVData {
  field1: "Date",
  field2: "Time",
  field3: "Time Zone",
  field4: "Gross Sales",
  field5: "Discounts",
  field6: "Service Charges",
  field7: "Net Sales",
  field8: "Gift Card Sales",
  field9: "Tax",
  field10: "Tip",
  field11: "Partial Refunds",
  field12: "Total Collected",
  field13: "Source",
  field14: "Card",
  field15: "Card Entry Methods",
  field16: "Cash",
  field17: "Square Gift Card",
  field18: "Other Tender",
  field19: "Other Tender Type",
  field20: "Other Tender Note",
  field21: "Fees",
  field22: "Net Total",
  field23: "Transaction ID",
  field24: "Payment ID",
  field25: "Card Brand",
  field26: "PAN Suffix",
  field27: "Device Name",
  field28: "Staff Name",
  field29: "Staff ID",
  field30: "Details",
  field31: "Description",
  field32: "Event Type",
  field33: "Deposit ID",
  field34: "Location",
  field35: "Dining Option",
  field36: "Fee Percentage Rate",
  field37: "Fee Fixed Rate",
  field38: "Refund Reason",
  field39: "Discount Name",
  field40: "Transaction Status",
  field41: "Fulfilment Note"
}

const TRANSFER_NOTE_MAX_LENGTH = 24;

@Component({
  selector: 'app-dialog-square-revenue',
  templateUrl: 'square-revenue.dialog.html',
  styleUrls: ['./square-revenue.dialog.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SquareRevenueDialogComponent implements OnDestroy {
  public apiBusy = false;
  public apiError = '';
  public apiMessage = '';
  public apiCandidates: SquarePayoutCandidate[] = [];
  public selectedPayoutId = '';
  private apiSubscription?: Subscription;
  private apiImported = false;

  public transaction: Transaction;

  public refunds: Transaction[];

  public feesTotal = 0;

  public totalReconilled = 0;
  public deposited = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<SquareRevenueDialogComponent>,
    public proofService: ProofService,
    public csvService: CSVService,
    public transactionService: TransactionsService,
    public jobService: JobService,
    private squareService: SquareService
    ) {
      this.transaction = data.transaction;
    }

  ngOnInit() {
    this.apiImported = Boolean(this.transaction.additional?.squarePayoutId);
    this.refresh();
  }
  ngOnDestroy() { this.apiSubscription?.unsubscribe(); }

  importFromSquare(payoutId?: string) {
    if (this.apiBusy) return;
    this.apiBusy = true; this.apiError = ''; this.apiMessage = '';
    this.apiSubscription = this.squareService.revenuePreview(this.transaction.source.sha1, payoutId).subscribe({
      next: result => {
        this.apiBusy = false; this.apiCandidates = result.candidates;
        if (!result.selected || !result.squareTransactions) {
          this.selectedPayoutId = '';
          this.apiMessage = 'Several transfers match the amount and date. Select the correct transfer below.';
          return;
        }
        this.transaction.squareTransactions = result.squareTransactions;
        this.apiImported = true;
        this.selectedPayoutId = result.selected.id;
        this.transaction.additional = { ...this.transaction.additional, squarePayoutId: result.selected.id,
          squareIgnoredTaxOnFeeNetTotal: result.ignoredTaxOnFeeNetTotal || 0 };
        this.refresh();
        const basis = result.selected.referenceMatch === 'full' ? 'reference, amount and date'
          : result.selected.referenceMatch === 'partial' ? 'truncated reference, amount and date'
          : payoutId ? 'your selection, amount and date' : 'unique amount and date; no reference match';
        this.apiMessage = `Loaded ${result.squareTransactions.length} entries from ${result.selected.id} (${basis}). Review the list, then Save or Commit.`;
      },
      error: error => { this.apiBusy = false; this.apiError = error?.error?.error || 'Unable to import from Square.'; }
    });
  }

  refresh() {
    if (this.transaction.squareTransactions == undefined) {
      this.transaction.squareTransactions = [];
    }

    for (let transaction of this.transaction.squareTransactions) {
      transaction.note = this.truncateTransferNote(transaction.note);
    }

    this.feesTotal = 0;
    this.deposited = this.transaction.amount;
    this.totalReconilled = 0;

    for (let transaction of this.transaction.squareTransactions) {
      this.totalReconilled += transaction.netTotal;
      this.feesTotal += transaction.fees;
    }
  }

  save() {
    if (this.apiBusy) return;
    this.transactionService.update(this.transaction).subscribe(r => {
      this.close("sorted");
    });
  }

  commit () {
    if (this.apiBusy) return;
    this.refresh();
    if (this.apiImported && this.transaction.squareTransactions.reduce((sum, row) => sum + row.totalCollected + row.fees, 0)
      + (this.transaction.additional?.squareIgnoredTaxOnFeeNetTotal || 0) !== this.transaction.amount) {
      this.apiError = 'The imported entries no longer reconcile to the deposit. Review the amounts before committing.';
      return;
    }
    let transfers: any[] = [];

    transfers.push({
      srcAccount: ACCOUNT.G72EXPENSES,  // Expenses
      destAccount: ACCOUNT.G72REVENUE, // Square Revenue
      amount: Math.abs(this.feesTotal / 100).toFixed(2),
      transactionDescription: "Square Fees"
    });

    for (let transaction of this.transaction.squareTransactions) {
      if (transaction.refund) {
        transfers.push({
          srcAccount: ACCOUNT.G72EXPENSES,  // Expenses
          destAccount: ACCOUNT.G72REVENUE, // Square Revenue
          amount: Math.abs(transaction.totalCollected / 100).toFixed(2),
          transactionDescription: transaction.note
        });
      }
    }

    for (let transaction of this.transaction.squareTransactions) {
      if (!transaction.refund && transaction.totalCollected !== 0) {
        transfers.push({
          srcAccount: ACCOUNT.G72REVENUE,  // Square Revenue
          destAccount: ACCOUNT.REVENUE, // Revenue
          amount: (transaction.totalCollected / 100).toFixed(2),
          transactionDescription: transaction.note
        });
      }
    }

    // Do one final validation
    let balance = this.transaction.amount / 100;
    for (let transfer of transfers) {
      balance -= transfer.amount;
    }
    
    balance = 0;

    if (balance == 0) {
      this.jobService.create({
        job: Job.BANK_TRANSFER,
        date: {
          start: new Date()
        },
        data: {
          transfers: transfers
        }
      }).subscribe(r=> {
        // Also mark all cash revenue as sorted
        this.transaction.sorted = true;

        this.transactionService.update(this.transaction).subscribe(r=>{
          this.dialogRef.close();
        });
      });
    }
  }

  submitProof(event: any) {
    if (this.apiBusy) return;
    this.apiImported = false; this.apiMessage = ''; this.apiError = ''; this.apiCandidates = [];
    if (this.transaction.additional) {
      delete this.transaction.additional.squarePayoutId;
      delete this.transaction.additional.squareIgnoredTaxOnFeeNetTotal;
    }

    this.csvService.fileToJSON(event.addedFiles[0]).subscribe((r: CSVData[]) => {

      this.transaction.squareTransactions = [];

      if (r == undefined) {
        return;
      }

      // Rejig the data
      for (let i = 1; i < r.length; i++) {
          // This is a refund
          const refund = strToCents(r[i].field12) < 0;

          this.transaction.squareTransactions.push({
            date: r[i].field1,
            time: r[i].field2,
            timeZone: r[i].field3,
            totalCollected: strToCents(r[i].field12),
            fees: strToCents(r[i].field21),
            netTotal: strToCents(r[i].field22),
            note: formatSquareNote(r[i].field31, refund),
            transactionID: r[i].field23, 
            paymentID: r[i].field24,
            cardBrand: r[i].field25,
            deviceName: r[i].field27,
            squareURL: r[i].field30,
            location: r[i].field34,
            transactionStatus: r[i].field40,
            refund: refund
          });
      }

      this.transactionService.update(this.transaction).subscribe(()=>{
        this.refresh();
      })
    })
  }

  close (action: string) {
    this.dialogRef.close(action);
  }

  updateTransferNote(transfer: SquareTransaction, note: string) {
    transfer.note = this.truncateTransferNote(note);
  }

  truncateTransferNote(note: string) {
    return (note || "").slice(0, TRANSFER_NOTE_MAX_LENGTH);
  }

  clear () {
    if (this.apiBusy) return;
    this.apiImported = false; this.apiCandidates = []; this.apiMessage = ''; this.apiError = '';
    if (this.transaction.additional) {
      delete this.transaction.additional.squarePayoutId;
      delete this.transaction.additional.squareIgnoredTaxOnFeeNetTotal;
    }
    this.transaction.squareTransactions = [];
    this.refresh();
  }

  markAsSorted () {
    this.transaction.sorted = true;

    this.save();
  }

  fuck () {
    this.transaction.sorted = false;

    this.save();
  }
}

function strToCents(amount: string) {
  return parseFloat(amount.replace("$", "").replace(",", "")) * 100
}

// Date: "2023-05-02"
// Time: "12:59:12"
// Time Zone: "Sydney"
// Total Collected: "$242.00"
// Fees: "-$4.60"
// Net Total: "$237.40"
// Description: "Custom Amount"


// Transaction ID: "ktg2HKQOzxceHFadOosjFvseV"
// Payment ID: "JSzuKt5GdjrgwFHtXzj7Qk6tvaB"
// Card Brand: "EFTPOS"
// PAN Suffix: "9418"
// Device Name: "Joe"

// Details: "https://squareup.com/dashboard/sales/transactions/ktg2HKQOzxceHFadOosjFvseV/by-unit/LGA7CT9GKBYMT"


// Event Type: "Payment"
// Deposit ID: "3ZC9F5DEWMDX5EX0TJYBZ4ZMXQT9"
// Location: "Bendigo PC Clinic"
// Transaction Status: "Complete"





// Date: "2023-05-02"
// Time: "12:59:12"
// Time Zone: "Sydney"
// Gross Sales: "$242.00"
// Discounts: "$0.00"
// Service Charges: "$0.00"
// Net Sales: "$237.49"
// Gift Card Sales: "$0.00"
// Tax: "$4.51"
// Tip: "$0.00"
// Partial Refunds: "$0.00"
// Total Collected: "$242.00"
// Source: "Point of Sale"
// Card: "$242.00"
// Card Entry Methods: "Tapped"
// Cash: "$0.00"
// Square Gift Card: "$0.00"
// Other Tender: "$0.00"
// Other Tender Type: ""
// Other Tender Note: ""
// Fees: "-$4.60"
// Net Total: "$237.40"
// Transaction ID: "ktg2HKQOzxceHFadOosjFvseV"
// Payment ID: "JSzuKt5GdjrgwFHtXzj7Qk6tvaB"
// Card Brand: "EFTPOS"
// PAN Suffix: "9418"
// Device Name: "Joe"
// Staff Name: ""
// Staff ID: ""
// Details: "https://squareup.com/dashboard/sales/transactions/ktg2HKQOzxceHFadOosjFvseV/by-unit/LGA7CT9GKBYMT"
// Description: "Custom Amount"
// Event Type: "Payment"
// Deposit ID: "3ZC9F5DEWMDX5EX0TJYBZ4ZMXQT9"
// Location: "Bendigo PC Clinic"
// Dining Option: ""
// Fee Percentage Rate: "1.9"
// Fee Fixed Rate: "$0.00"
// Refund Reason: ""
// Discount Name: ""
// Transaction Status: "Complete"
// Fulfilment Note: ""

